import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@core/services/auth.service';
import { Subscription, take } from 'rxjs';
import { UserSessionComponent } from '../user-session/user-session.component';
import { ReEnrollmentRequestsComponent } from '../pending-requests/re-enrollment-requests/re-enrollment-requests.component';
import { Router } from '@angular/router';
import { Permission } from '@core/enums/permissions';
import { PermissionService } from '@core/services/permission.service';
import { RatingService } from '@core/services/rating.service';
import { StudentRequestService } from '@core/services/student-request.service';
import { StudentRequestReason } from '@core/enums/student-request-reason';
import { PortfolioMessageService } from '@core/services/portfolio-message.service';
import { IPortfolioMessageResponse, IPortfolioMessageSearchRequest } from '@core/interfaces/iportfolio-message';
import { MessageStatus } from '@core/enums/message-status';
import { UserService } from '@core/services/user.service';
import { StateService } from '@core/services/state.service';
import { LookupService } from '@core/services/lookup.service';
import { IGlobalAnnouncement } from '@core/interfaces/iglobal-announcement';
import { TranslocoService } from '@jsverse/transloco';
@Component({
    selector: 'drdp-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardComponent implements OnInit {
  private subscriptions = new Subscription();
  user = this.authService.getCurrentUser();
  isAdmin = this.authService.isAdmin();
  isSuperAdmin = this.authService.isSuperAdmin();
  isTeacher = this.authService.isTeacher();
  ratingCount = '0/0';
  hasPendingTransfers: boolean = false;
  hasPendingUnEnrollments: boolean = false;
  hasPendingReEnrollments: boolean = false;
  hasPendingChildReEnrollments: boolean = false;
  hasPendingReleaseRequest: boolean = false;
  hasNewMessages: boolean = false;
  newMessagesIcon = 'assets/images/misc/message-notification.svg';
  icon = 'assets/images/misc/message.svg';
  isSignedUp: boolean = false;
  isStateDashboardEnabled: boolean = false;
  announcements: IGlobalAnnouncement[] = [];
  canEditLanguage: boolean = this.permissionService.checkPermission(
    Permission.EditLanguageSettings
  );
  canViewLanguage: boolean = this.permissionService.checkPermission(
    Permission.ViewLanguageSettings
  );

  canViewNewsItem: boolean = this.permissionService.checkPermission(
    Permission.ViewNewsItem
  );

  canViewLockedChildren: boolean = this.permissionService.checkPermission(
    Permission.ViewTotalLockedChildren
  );

  isPendingIndicatorHidden: boolean = this.permissionService.checkPermission(
    Permission.HidePendingIndicator
  );

  canViewPortfolioMessages: boolean = this.permissionService.checkPermission(
    Permission.AccessToPortfolioMessages
  ) || this.permissionService.checkPermission(
    Permission.AccessToAllPortfolioMessageBoards
  );

  constructor(
    public modal: MatDialog,
    private authService: AuthService,
    private router: Router,
    private permissionService: PermissionService,
    private ratingService: RatingService,
    private studentRequestService: StudentRequestService,
    private portfolioMessageService: PortfolioMessageService,
    private userService: UserService,
    private stateService: StateService,
    private lookupService: LookupService,
    private translocoService: TranslocoService
  ) {
    this.lookupService.getActiveGlobalAnnouncements().pipe(take(1)).subscribe((announcements: IGlobalAnnouncement[]) => {
      this.announcements = announcements.filter(announcement => !announcement.stateId || announcement.stateId == this.user.stateId);
    });
  }

  myDashboard = [
    {
      label: this.translocoService.translate('user.dashboard.tiles.classrooms'),
      icon: 'uil uil-diary',
      link: '/classrooms',
      access: Permission.ViewDashboardClassrooms,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.children'),
      icon: 'uil uil-diary',
      link: '/children',
      access: Permission.AccessToChildrenPAge,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.myRatings'),
      icon: 'uil uil-comment-add',
      link: '/ratings',
      access: Permission.ViewMyRatings,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.portfolio'),
      icon: 'uil uil-telescope',
      link: '/portfolio',
      access: Permission.ViewObservations,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.pendingTransfers'),
      icon: 'uil uil-sign-in-alt',
      link: '/pending-transfers',
      access: Permission.ViewPendingTransfers,
      hasPendingRequest: this.hasPendingTransfers,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.pendingWithdraws'),
      icon: 'uil uil-user-minus',
      link: '/pending-withdraws',
      access: Permission.ViewPendingExits,
      hasPendingRequest: this.hasPendingUnEnrollments,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.pendingClassroomReEnrollments'),
      icon: 'uil uil-file-redo-alt',
      link: '/pending-re-enrollment',
      access: Permission.ViewPendingReEnrollments,
      hasPendingRequest: this.hasPendingReEnrollments,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.pendingChildReEnrollments'),
      icon: 'uil uil-redo',
      link: '/pending-child-re-enrollment',
      access: Permission.ViewPendingReEnrollments,
      hasPendingRequest: this.hasPendingChildReEnrollments,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.pendingReleaseRequests'),
      icon: 'uil uil-sign-out-alt',
      link: '/pending-releases',
      access: Permission.ViewPendingReleaseRequests,
      hasPendingRequest: this.hasPendingReleaseRequest,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.childRequests'),
      icon: 'uil uil-sign-alt',
      link: '/child-requests',
      access: Permission.AccessChildRequestsPage,
    },
    {
      label: this.translocoService.translate('user.dashboard.tiles.pendingPortfolioEvidenceRequests'),
      icon: 'uil uil-image-v',
      link: '/portfolio-evidence-requests',
      access: Permission.PendingPortfolioRequests,
    },
  ];

  languageSettingsInfo = [
    {
      label: this.translocoService.translate('user.dashboard.languageOptions.viewSiteInSpanish'),
      access: true,
    },
    {
      label: this.translocoService.translate('user.dashboard.languageOptions.ratingSheetInSpanish'),
      access: false,
    },
  ];

  ngOnInit(): void { 
    if (this.user.stateDashboardPermissionId) {
        this.permissionService.isStateDashboardPermissionEnabled(this.user.stateDashboardPermissionId)
        .pipe(take(1)).subscribe((res: boolean) => {
          if (res) {
            this.isStateDashboardEnabled = res;
          }
      });
    }
      
    this.userService.getSignUpStatus().pipe(take(1)).subscribe((result: boolean) => {
        this.isSignedUp = result;
      });

    if (this.user.sessionCount > 1) {
      this.openSessionModel();
    }
    this.getRatingCount();

    if (!this.isPendingIndicatorHidden) this.getPendingRequest();

    if (this.canViewPortfolioMessages) this.checkNewMessages();
  }
  

  checkNewMessages(): void {
    const payload: IPortfolioMessageSearchRequest = {
      itemsPerPage: 100,
      pageIndex: 1
    }
    this.portfolioMessageService
      .getPortfolioMessagesByUserId(payload)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.hasNewMessages = res.hasNewMessages;
        this.icon = this.hasNewMessages ? this.newMessagesIcon : this.icon;
      })
  }

  toggleSwitchAction(info: any) {}

  openSessionModel(): void {
    const modalRef = this.modal.open(UserSessionComponent);
    this.subscriptions.add(modalRef.afterClosed().subscribe());
  }

  buttonClick(value: string) {}

  navigate(component: any) {
    this.router.navigate([component]);
  }

  getRatingCount() {
    if (this.canViewLockedChildren) {
      this.ratingService
        .getLockCount()
        .pipe(take(1))
        .subscribe((result) => {
          this.ratingCount = result.count;
        });
    }
  }

  getPendingRequest() {
    const pendingRequests = [
      {
        reason: StudentRequestReason.ReEnrollStudent,
        label: this.translocoService.translate('user.dashboard.tiles.pendingChildReEnrollments'),
      },
      {
        reason: StudentRequestReason.ReEnroll,
        label: this.translocoService.translate('user.dashboard.tiles.pendingClassroomReEnrollments'),
      },
      {
        reason: StudentRequestReason.Release,
        label: this.translocoService.translate('user.dashboard.tiles.pendingReleaseRequests'),
      },
      { reason: StudentRequestReason.Transfer, label: this.translocoService.translate('user.dashboard.tiles.pendingTransfers') },
      { reason: StudentRequestReason.Drop, label: this.translocoService.translate('user.dashboard.tiles.pendingWithdraws') },
    ];

    pendingRequests.forEach((request) => {
      this.studentRequestService
        .getHasPendingRequest(request.reason)
        .pipe(take(1))
        .subscribe((result) => {
          if (result) {
            const dashboardItem = this.myDashboard.find(
              (item) => item.label === request.label
            );
            if (dashboardItem) {
              dashboardItem.hasPendingRequest = result;
            }
          }
        });
    });
  }

  onCheckboxChange(): void {
    this.isSignedUp = !this.isSignedUp;
    this.userService.updateSignUpStatus(this.isSignedUp).pipe(take(1)).subscribe();
  }
}
